import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useDeleteList, useLeaveList, useRenameList } from '../../api/hooks';
import type { ListDetail } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { IconLeave, IconPencil, IconTrash, IconUserPlus } from '../../ui/icons';
import { Sheet, SheetAction } from '../../ui/Sheet';
import { toast } from '../../ui/toast';

type Mode = 'menu' | 'rename' | 'delete' | 'leave';

export function ListOptionsSheet({
  list,
  open,
  onClose,
  onShare,
}: {
  list: ListDetail;
  open: boolean;
  onClose: () => void;
  onShare: () => void;
}) {
  const [mode, setMode] = useState<Mode>('menu');
  const close = () => {
    onClose();
    setMode('menu');
  };
  const isOwner = list.role === 'owner';

  const titles: Record<Mode, string> = {
    menu: list.title,
    rename: 'Rename list',
    delete: 'Delete this list?',
    leave: 'Leave this list?',
  };

  return (
    <Sheet open={open} onClose={close} title={titles[mode]}>
      {mode === 'menu' && (
        <div className="-mx-1 space-y-1">
          {isOwner ? (
            <>
              <SheetAction icon={<IconPencil size={20} />} onClick={() => setMode('rename')}>
                Rename
              </SheetAction>
              <SheetAction
                icon={<IconUserPlus size={20} />}
                onClick={() => {
                  close();
                  onShare();
                }}
              >
                Share &amp; members
              </SheetAction>
              <SheetAction icon={<IconTrash size={20} />} tone="danger" onClick={() => setMode('delete')}>
                Delete list
              </SheetAction>
            </>
          ) : (
            <SheetAction icon={<IconLeave size={20} />} tone="danger" onClick={() => setMode('leave')}>
              Leave list
            </SheetAction>
          )}
        </div>
      )}
      {mode === 'rename' && <RenameForm list={list} onDone={close} onCancel={() => setMode('menu')} />}
      {mode === 'delete' && <ConfirmDelete list={list} onCancel={() => setMode('menu')} />}
      {mode === 'leave' && <ConfirmLeave list={list} onCancel={() => setMode('menu')} />}
    </Sheet>
  );
}

function RenameForm({ list, onDone, onCancel }: { list: ListDetail; onDone: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState(list.title);
  const [error, setError] = useState<string | null>(null);
  const rename = useRenameList(list.id);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (!t) return setError('The name can’t be empty.');
    if (t.length > 100) return setError('Keep it under 100 characters.');
    if (t === list.title) return onDone();
    rename.mutate(t, {
      onSuccess: () => {
        toast.success('List renamed');
        onDone();
      },
    });
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <Field
        data-autofocus
        label="List name"
        name="title"
        autoComplete="off"
        enterKeyHint="done"
        maxLength={100}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        error={error}
      />
      <div className="flex gap-3">
        <Button variant="secondary" size="lg" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="lg" className="flex-1" loading={rename.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}

function ConfirmDelete({ list, onCancel }: { list: ListDetail; onCancel: () => void }) {
  const del = useDeleteList(list.id);
  const navigate = useNavigate();
  return (
    <div className="space-y-5">
      <p className="text-muted">
        “{list.title}” and its {list.itemCount} item{list.itemCount === 1 ? '' : 's'} will be removed for everyone it’s
        shared with. This can’t be undone.
      </p>
      <div className="flex gap-3">
        <Button variant="secondary" size="lg" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="danger"
          size="lg"
          className="flex-1"
          loading={del.isPending}
          onClick={() =>
            del.mutate(undefined, {
              onSuccess: () => {
                toast.success(`Deleted “${list.title}”`);
                navigate('/', { replace: true });
              },
            })
          }
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

function ConfirmLeave({ list, onCancel }: { list: ListDetail; onCancel: () => void }) {
  const { user } = useAuth();
  const leave = useLeaveList(list.id, user?.id);
  const navigate = useNavigate();
  return (
    <div className="space-y-5">
      <p className="text-muted">You’ll lose access to “{list.title}” until someone invites you again.</p>
      <div className="flex gap-3">
        <Button variant="secondary" size="lg" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="danger"
          size="lg"
          className="flex-1"
          loading={leave.isPending}
          onClick={() =>
            leave.mutate(undefined, {
              onSuccess: () => {
                toast.success(`You left “${list.title}”`);
                navigate('/', { replace: true });
              },
            })
          }
        >
          Leave
        </Button>
      </div>
    </div>
  );
}
